/**
 * HEDIS / Pre-built Template Library — Demo Flow 4.
 *
 * Card grid showing system library templates filtered by category, with
 * a "Use this template" action that POSTs /api/templates/{id}/clone and
 * returns the new draft template id to the caller (typically routed
 * into the campaign builder).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { authFetchJson, authFetch } from '../../lib/authFetch';

interface LibraryTemplate {
  id: number;
  name: string;
  description: string | null;
  template_type: string;
  communication_method: string;
  subject: string | null;
  body: string;
  default_language: string;
  gallery_summary: string | null;
  category_key: string | null;
  category_name: string | null;
  measure_id: number | null;
  measure_id_hedis: string | null;
  measure_name: string | null;
}

const CATEGORIES = [
  { key: 'all',                 label: 'All' },
  { key: 'hedis',               label: 'HEDIS Gaps' },
  { key: 'cahps',               label: 'CAHPS' },
  { key: 'welcome',             label: 'Welcome' },
  { key: 'member_enrollment',   label: 'Member Enrollment' },
  { key: 'ecm',                 label: 'CalAIM ECM' },
  { key: 'community_supports',  label: 'CalAIM CS' },
  { key: 'case_management',     label: 'Case Management' },
  { key: 'customer_support',    label: 'Customer Support' },
  { key: 'adherence',           label: 'Adherence' },
  { key: 'pharmacy',            label: 'Pharmacy' },
  { key: 'preventive',          label: 'Preventive' },
  { key: 'behavioral_health',   label: 'Behavioral Health' },
  { key: 'maternity',           label: 'Maternity' },
  { key: 'sdoh',                label: 'SDOH / Social Needs' },
  { key: 'transitions_of_care', label: 'Transitions of Care' },
  { key: 'referral_followup',   label: 'Referrals' },
  { key: 'redetermination',     label: 'Redetermination' },
];

const LANG_FLAG: Record<string, string> = { en: '🇺🇸', es: '🇲🇽' };

interface Props {
  cboId: number;
  currentUserId: number;
  onCloned?: (newTemplateId: number, source: LibraryTemplate) => void;
}

export const TemplateLibraryGallery: React.FC<Props> = ({ cboId, currentUserId, onCloned }) => {
  const [category, setCategory] = useState<string>('all');
  const [language, setLanguage] = useState<string>('en');
  const [templates, setTemplates] = useState<LibraryTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState<number | null>(null);
  const [previewTpl, setPreviewTpl] = useState<LibraryTemplate | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createBumpKey, setCreateBumpKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (cboId) params.set('cbo_id', String(cboId));
    if (category && category !== 'all') params.set('category', category);
    if (language) params.set('language', language);
    authFetchJson<LibraryTemplate[]>(`/api/templates/library?${params.toString()}`)
      .then(setTemplates)
      .catch((e) => { console.warn('library fetch failed', e); setTemplates([]); })
      .finally(() => setLoading(false));
  }, [cboId, category, language, createBumpKey]);

  const handleClone = async (tpl: LibraryTemplate) => {
    // Clone the universal template into the org's own template list. This
    // is the only API call — engagement / campaign creation is intentionally
    // separate so the import handoff lands on Communication Templates (where
    // the user can edit), not the campaign builder.
    setWorking(tpl.id);
    try {
      const res = await authFetch(`/api/templates/${tpl.id}/clone`, {
        method: 'POST',
        jsonBody: { cbo_id: cboId, user_id: currentUserId, name_override: `${tpl.name} — Customized` },
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${errText || res.statusText}`);
      }
      const cloned = (await res.json().catch(() => null)) as { new_template_id?: number } | null;
      const newId = cloned?.new_template_id ?? 0;

      if (onCloned) onCloned(newId, tpl);
      else {
        // Fallback for callers that don't pass onCloned — land on the
        // org's templates editor so the user can see the new copy.
        const w = window as any;
        if (typeof w.__navigateWithHistory === 'function') w.__navigateWithHistory('communication-templates');
      }
    } catch (e) {
      console.error('Import to my org failed:', e);
      alert(`Couldn't import this template: ${e instanceof Error ? e.message : 'unknown error'}`);
    } finally {
      setWorking(null);
    }
  };

  const goToTemplatesEditor = () => {
    const w = window as any;
    if (typeof w.__navigateWithHistory === 'function') w.__navigateWithHistory('communication-templates');
    else window.location.hash = '#communication-templates';
  };

  return (
    <div>
      <div className="mb-4 rounded-lg border border-v2border bg-paper-2 p-4 text-sm text-ink">
        <div className="mb-1 inline-block rounded bg-v2accent-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-v2accent-ink">
          Universal · cross-organization
        </div>
        <div className="mb-2 text-base font-semibold">
          A shared library of outreach templates — every organization can browse and import
        </div>
        <p className="mb-3 text-xs text-ink-500">
          These templates are curated and maintained centrally. Any organization can preview them
          here and click <strong>Import to my org</strong> to copy a template into their own
          editable library. Once imported, your org owns the copy — edit, translate, version, and
          launch as you would any other template.
        </p>
        <ol className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-5">
          <li className="rounded-lg border border-v2border bg-surface px-3 py-2">
            <div className="font-bold text-ink">1. Browse</div>
            <div className="text-gray-700">Filter by use case, HEDIS measure, or language.</div>
          </li>
          <li className="rounded-lg border border-v2border bg-surface px-3 py-2">
            <div className="font-bold text-ink">2. Preview</div>
            <div className="text-gray-700">Read the rendered SMS / email on each card.</div>
          </li>
          <li className="rounded-lg border border-v2border bg-surface px-3 py-2">
            <div className="font-bold text-ink">3. Import</div>
            <div className="text-gray-700"><strong>Import to my org</strong> copies the template into your org's library.</div>
          </li>
          <li className="rounded-lg border border-v2border bg-surface px-3 py-2">
            <div className="font-bold text-ink">4. Customize</div>
            <div className="text-gray-700">Edit body, request approval, suppress already-completed members.</div>
          </li>
          <li className="rounded-lg border border-v2border bg-surface px-3 py-2">
            <div className="font-bold text-ink">5. Launch</div>
            <div className="text-gray-700">Approve, schedule, dispatch — every step audit-logged.</div>
          </li>
        </ol>
        <p className="mt-3 text-xs">
          Library cards are read-only across orgs. Importing sets{' '}
          <span className="font-mono">template_kind=&apos;cloned&apos;</span> with a{' '}
          <span className="font-mono">cloned_from_id</span> pointer back to the source; full CRUD
          on your org-owned copies lives in{' '}
          <button onClick={goToTemplatesEditor} className="font-semibold underline hover:text-v2accent">
            Communication Templates →
          </button>
        </p>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {CATEGORIES.map(c => (
          <button
            key={c.key}
            onClick={() => setCategory(c.key)}
            className={`rounded-full px-3 py-1 text-sm transition ${
              category === c.key
                ? 'bg-v2accent text-white'
                : 'bg-paper-2 text-ink-700 hover:text-ink'
            }`}
          >
            {c.label}
          </button>
        ))}
        <span className="ml-auto inline-flex items-center gap-1 text-xs text-gray-500">
          Language:
          <select
            value={language}
            onChange={e => setLanguage(e.target.value)}
            className="rounded border border-gray-300 bg-white px-2 py-0.5 text-xs"
          >
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </span>
        <button onClick={() => setShowCreate(true)}
          className="rounded border border-v2border bg-surface px-3 py-1.5 text-sm font-medium text-ink hover:bg-paper-2">
          + Add to library
        </button>
      </div>

      {showCreate && (
        <AddLibraryTemplateModal
          cboId={cboId}
          currentUserId={currentUserId}
          defaultCategory={category !== 'all' ? category : 'hedis'}
          defaultLanguage={language}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            setCreateBumpKey(k => k + 1);
          }} />
      )}

      {loading && <p className="text-sm text-gray-500">Loading library…</p>}
      {!loading && templates.length === 0 && (
        <p className="text-sm text-gray-500">No templates match these filters.</p>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {templates.map(t => (
          <div
            key={t.id}
            role="button"
            tabIndex={0}
            onClick={() => setPreviewTpl(t)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPreviewTpl(t); } }}
            className="cursor-pointer rounded-xl border border-v2border bg-surface p-4 shadow-sm transition hover:border-v2accent hover:shadow-md focus:outline-none focus:ring-2 focus:ring-v2accent"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-ink-500">
                  {t.category_name || t.category_key}
                </div>
                <div className="mt-1 text-base font-semibold text-gray-900">{t.name}</div>
                {t.measure_id_hedis && (
                  <div className="mt-1 inline-block rounded bg-paper-2 px-2 py-0.5 text-[10px] font-bold uppercase text-ink-700">
                    HEDIS {t.measure_id_hedis}
                  </div>
                )}
              </div>
              <span className="text-2xl" title={t.default_language}>
                {LANG_FLAG[t.default_language] || '🌐'}
              </span>
            </div>
            {t.gallery_summary && (
              <p className="mt-2 text-sm text-gray-600">{t.gallery_summary}</p>
            )}
            <div className="mt-3 line-clamp-3 rounded bg-gray-50 px-3 py-2 text-xs text-gray-700">
              {t.body}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] uppercase text-gray-600">
                {t.communication_method}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); setPreviewTpl(t); }}
                className="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
              >
                Preview
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleClone(t); }}
                disabled={working === t.id}
                className="ml-auto rounded bg-v2accent px-3 py-1.5 text-sm font-medium text-white hover:bg-v2accent/90 disabled:opacity-50"
              >
                {working === t.id ? 'Importing…' : 'Import to my org'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {previewTpl && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setPreviewTpl(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-lg bg-white p-6 shadow-xl"
          >
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-ink-500">
                  {previewTpl.category_name || previewTpl.category_key}
                </div>
                <h2 className="mt-0.5 text-lg font-semibold text-gray-900">{previewTpl.name}</h2>
                {previewTpl.measure_id_hedis && (
                  <div className="mt-2 inline-block rounded bg-paper-2 px-2 py-0.5 text-[11px] font-bold uppercase text-ink-700">
                    HEDIS {previewTpl.measure_id_hedis} · {previewTpl.measure_name}
                  </div>
                )}
              </div>
              <button onClick={() => setPreviewTpl(null)} aria-label="Close"
                className="text-gray-400 hover:text-gray-700">✕</button>
            </div>

            {previewTpl.gallery_summary && (
              <p className="mb-3 text-sm text-gray-700">{previewTpl.gallery_summary}</p>
            )}
            {previewTpl.description && previewTpl.description !== previewTpl.gallery_summary && (
              <p className="mb-3 text-xs text-gray-500">{previewTpl.description}</p>
            )}

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
                <div className="font-bold uppercase text-gray-500">Channel</div>
                <div className="mt-0.5 text-gray-900">{previewTpl.communication_method}</div>
              </div>
              <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2">
                <div className="font-bold uppercase text-gray-500">Default language</div>
                <div className="mt-0.5 text-gray-900">
                  {LANG_FLAG[previewTpl.default_language] || '🌐'} {previewTpl.default_language}
                </div>
              </div>
            </div>

            {previewTpl.subject && (
              <div className="mt-3">
                <div className="text-xs font-bold uppercase text-gray-500">Subject</div>
                <div className="mt-0.5 rounded border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900">
                  {previewTpl.subject}
                </div>
              </div>
            )}

            <div className="mt-3">
              <div className="text-xs font-bold uppercase text-gray-500">Body</div>
              <pre className="mt-0.5 whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900">{previewTpl.body}</pre>
              <p className="mt-1 text-[11px] text-gray-500">
                Personalization tokens like <span className="font-mono">{'{first_name}'}</span> are
                resolved against each member at send time.
              </p>
            </div>

            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setPreviewTpl(null)}
                className="rounded bg-gray-100 px-3 py-1.5 text-sm">Close</button>
              <button
                onClick={() => { const t = previewTpl; setPreviewTpl(null); handleClone(t); }}
                disabled={working === previewTpl.id}
                className="rounded bg-v2accent px-3 py-1.5 text-sm font-medium text-white hover:bg-v2accent/90 disabled:opacity-50"
              >
                {working === previewTpl.id ? 'Importing…' : 'Import to my org'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Add new library template — modal form
// ============================================================================
const HEDIS_MEASURES = [
  { id: '', label: '— none —' },
  { id: 'EED', label: 'EED — Diabetic Eye Exam' },
  { id: 'HBD', label: 'HBD — HbA1c Control' },
  { id: 'BCS', label: 'BCS — Breast Cancer Screening' },
  { id: 'COL', label: 'COL — Colorectal Screening' },
  { id: 'CIS', label: 'CIS — Childhood Immunization' },
  { id: 'SAA', label: 'SAA — Schizophrenia Adherence' },
  { id: 'SPC', label: 'SPC — Statin Therapy for Cardiovascular Disease' },
  { id: 'ADH-STA', label: 'ADH-STA — Statin Adherence' },
];

const CATEGORY_OPTIONS = [
  { key: 'hedis',               label: 'HEDIS Gaps' },
  { key: 'cahps',               label: 'CAHPS' },
  { key: 'welcome',             label: 'Welcome' },
  { key: 'member_enrollment',   label: 'Member Enrollment' },
  { key: 'ecm',                 label: 'CalAIM ECM' },
  { key: 'community_supports',  label: 'CalAIM CS' },
  { key: 'case_management',     label: 'Case Management' },
  { key: 'customer_support',    label: 'Customer Support' },
  { key: 'adherence',           label: 'Adherence' },
  { key: 'pharmacy',            label: 'Pharmacy' },
  { key: 'preventive',          label: 'Preventive' },
  { key: 'behavioral_health',   label: 'Behavioral Health' },
  { key: 'maternity',           label: 'Maternity' },
  { key: 'sdoh',                label: 'SDOH / Social Needs' },
  { key: 'transitions_of_care', label: 'Transitions of Care' },
  { key: 'referral_followup',   label: 'Referrals' },
  { key: 'redetermination',     label: 'Redetermination' },
];

const COMM_METHODS = [
  { key: 'sms', label: 'SMS' },
  { key: 'email', label: 'Email' },
  { key: 'voice', label: 'Voice / IVR' },
  { key: 'whatsapp', label: 'WhatsApp' },
];

const AddLibraryTemplateModal: React.FC<{
  cboId: number;
  currentUserId: number;
  defaultCategory: string;
  defaultLanguage: string;
  onClose: () => void;
  onCreated: () => void;
}> = ({ cboId, currentUserId, defaultCategory, defaultLanguage, onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: '',
    body: '',
    subject: '',
    description: '',
    gallery_summary: '',
    communication_method: 'sms',
    category_key: defaultCategory === 'all' ? 'hedis' : defaultCategory,
    measure_id_hedis: '',
    default_language: defaultLanguage || 'en',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEmail = form.communication_method === 'email';

  const submit = async () => {
    if (!form.name.trim() || !form.body.trim()) {
      setError('Name and message body are required.');
      return;
    }
    setBusy(true); setError(null);
    try {
      const res = await authFetch('/api/templates/library', {
        method: 'POST',
        jsonBody: {
          cbo_id: cboId,
          user_id: currentUserId,
          name: form.name.trim(),
          body: form.body.trim(),
          subject: isEmail ? (form.subject.trim() || null) : null,
          description: form.description.trim() || null,
          gallery_summary: form.gallery_summary.trim() || null,
          communication_method: form.communication_method,
          template_type: 'outreach',
          category_key: form.category_key || null,
          measure_id_hedis: form.measure_id_hedis || null,
          default_language: form.default_language,
        },
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status}: ${txt || res.statusText}`);
      }
      onCreated();
    } catch (e: any) {
      console.error('library template create failed', e);
      setError(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-auto rounded-lg bg-white p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold">Add a new template to the library</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">✕</button>
        </div>
        <p className="mb-4 text-xs text-gray-500">
          New library entries are scoped to your organization — they show up
          alongside the system-seeded templates in your gallery but stay
          private to your org. Use this for org-specific reminder copy you
          want CSRs to be able to clone repeatedly.
        </p>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold uppercase text-gray-500">Template name <span className="text-red-600">*</span></label>
            <input value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder='e.g. "Mammogram Reminder Q2"'
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">Category</label>
              <select value={form.category_key}
                onChange={e => setForm({ ...form, category_key: e.target.value })}
                className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm">
                {CATEGORY_OPTIONS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">Channel</label>
              <select value={form.communication_method}
                onChange={e => setForm({ ...form, communication_method: e.target.value })}
                className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm">
                {COMM_METHODS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">Language</label>
              <select value={form.default_language}
                onChange={e => setForm({ ...form, default_language: e.target.value })}
                className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm">
                <option value="en">🇺🇸 English</option>
                <option value="es">🇲🇽 Español</option>
                <option value="vi">🇻🇳 Tiếng Việt</option>
                <option value="zh">🇨🇳 中文</option>
                <option value="ar">🇸🇦 العربية</option>
                <option value="ko">🇰🇷 한국어</option>
              </select>
            </div>
          </div>

          {form.category_key === 'hedis' && (
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">HEDIS measure (optional)</label>
              <select value={form.measure_id_hedis}
                onChange={e => setForm({ ...form, measure_id_hedis: e.target.value })}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm">
                {HEDIS_MEASURES.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            </div>
          )}

          {isEmail && (
            <div>
              <label className="text-xs font-bold uppercase text-gray-500">Email subject</label>
              <input value={form.subject}
                onChange={e => setForm({ ...form, subject: e.target.value })}
                className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm" />
            </div>
          )}

          <div>
            <label className="text-xs font-bold uppercase text-gray-500">Message body <span className="text-red-600">*</span></label>
            <textarea value={form.body}
              onChange={e => setForm({ ...form, body: e.target.value })}
              rows={5}
              placeholder='Use {first_name} for member name. e.g. "Hi {first_name}, …"'
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm" />
            <p className="mt-1 text-[10px] text-gray-500">
              Supports {'{first_name}'} and other merge fields. Variant translations
              can be added after creation via the Campaign Builder.
            </p>
          </div>

          <div>
            <label className="text-xs font-bold uppercase text-gray-500">Gallery summary (optional)</label>
            <input value={form.gallery_summary}
              onChange={e => setForm({ ...form, gallery_summary: e.target.value })}
              placeholder="Short caption shown on the card under the name"
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm" />
          </div>

          <div>
            <label className="text-xs font-bold uppercase text-gray-500">Internal description (optional)</label>
            <textarea value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              rows={2}
              placeholder="Notes for CSRs / managers (not shown to members)"
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm" />
          </div>
        </div>

        {error && (
          <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-800">
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose}
            className="rounded bg-gray-100 px-3 py-1.5 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy || !form.name.trim() || !form.body.trim()}
            className="rounded bg-v2accent px-3 py-1.5 text-sm text-white hover:bg-v2accent/90 disabled:opacity-50">
            {busy ? 'Saving…' : 'Save to library'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateLibraryGallery;
